import { formatCurrency } from "@/lib/currency";
import { useState, useRef, useCallback } from "react";
import { TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SUBSCRIPTION_TIERS, SubscriptionTierKey } from "@/lib/subscriptionTiers";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PlanChangeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetTierKey: SubscriptionTierKey;
  isTrialing: boolean;
  isDowngrade?: boolean;
  renewalDateFormatted?: string;
  /** Called after a successful upgrade (before reload) */
  onSuccess?: () => void;
}

const RETRY_DELAY_MS = 1000;
const LOCK_POLL_DELAY_MS = 1500;

async function invokeCheckout(tier: typeof SUBSCRIPTION_TIERS[SubscriptionTierKey], targetTierKey: SubscriptionTierKey, accessToken: string) {
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    headers: { Authorization: `Bearer ${accessToken}` },
    body: { priceId: tier.priceId, tierKey: targetTierKey },
  });
  if (error) throw error;
  return data;
}

async function invokeCheckSubscription(accessToken: string) {
  const { data, error } = await supabase.functions.invoke("check-subscription", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) throw error;
  return data;
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export function PlanChangeConfirmDialog({
  open,
  onOpenChange,
  targetTierKey,
  isTrialing,
  isDowngrade = false,
  renewalDateFormatted,
  onSuccess,
}: PlanChangeConfirmDialogProps) {
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("Processing…");
  const inflightRef = useRef(false);
  const tier = SUBSCRIPTION_TIERS[targetTierKey];

  const handleSuccess = useCallback((message: string) => {
    toast.success(message);
    onSuccess?.();
    onOpenChange(false);
    window.location.reload();
  }, [onSuccess, onOpenChange]);

  const handleConfirm = async () => {
    // Single-flight guard
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    setStatusText("Processing…");

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error("Please log in to change your plan");
        return;
      }
      const token = sessionData.session.access_token;

      let data: any;
      try {
        data = await invokeCheckout(tier, targetTierKey, token);
      } catch (firstErr: any) {
        // Check for 409 lock conflict
        if (firstErr?.status === 409 || firstErr?.message?.includes("409") || firstErr?.context?.status === 409) {
          setStatusText("Processing your plan change…");
          toast.info("Processing your plan change…");
          await delay(LOCK_POLL_DELAY_MS);
          const subData = await invokeCheckSubscription(token);
          if (subData?.subscription_tier === targetTierKey) {
            handleSuccess(`Switched to ${tier.name} plan!`);
            return;
          }
          // Lock released but tier not updated — allow retry
          toast.info("Please try again");
          return;
        }

        // Auto-retry once for transient/network/500 errors
        const isTransient = !firstErr?.status || firstErr?.status >= 500 || firstErr?.message?.includes("FunctionsFetchError");
        if (isTransient) {
          setStatusText("Network issue — retrying…");
          toast.info("Network issue — retrying…");
          await delay(RETRY_DELAY_MS);
          try {
            data = await invokeCheckout(tier, targetTierKey, token);
          } catch (retryErr: any) {
            console.error("Plan change retry failed:", retryErr);
            toast.error("Could not complete plan change", {
              description: "Please check your connection and try again.",
            });
            return;
          }
        } else {
          throw firstErr;
        }
      }

      // --- Handle response ---

      // Downgrade scheduled
      if (data?.downgradeScheduled && !data?.dbSyncPending) {
        const effectiveDate = data.effectiveDate
          ? new Date(data.effectiveDate).toLocaleDateString("en-GB", {
              day: "numeric", month: "long", year: "numeric",
            })
          : "your next renewal date";
        toast.success("Plan change scheduled", {
          description: `Your plan will switch to ${tier.name} on ${effectiveDate}. You keep your current features until then.`,
        });
        onSuccess?.();
        onOpenChange(false);
        window.location.reload();
        return;
      }

      // Payment confirmation required (SCA / 3DS)
      if (data?.paymentRequired && data?.clientSecret) {
        setStatusText("Payment confirmation required");
        toast.info("Payment confirmation required", {
          description: "Please complete the payment to activate your new plan.",
        });
        // Close dialog — the payment flow is handled externally
        onOpenChange(false);
        return;
      }

      // Stripe changed the plan but the account record is not yet confirmed.
      // Never report success or allow a retry; poll for the webhook recovery.
      if (data?.dbSyncPending) {
        setStatusText("Confirming your new plan…");
        for (let i = 0; i < 3; i++) {
          await delay(LOCK_POLL_DELAY_MS * (i + 1));
          try {
            const sub = await invokeCheckSubscription(token);
            if (!data.downgradeScheduled && sub?.subscription_tier === targetTierKey) {
              handleSuccess(`Switched to ${tier.name} plan!`);
              return;
            }
          } catch { /* keep waiting */ }
        }
        toast.info("Your plan change is being finalised", {
          description: "Stripe has accepted the change. Your account will update shortly — please don't submit it again. Contact support if it hasn't updated within an hour.",
          duration: 12000,
        });
        onOpenChange(false);
        return;
      }

      // Immediate upgrade succeeded
      if (data?.upgraded) {
        handleSuccess(`Switched to ${tier.name} plan!`);
        return;
      }

      // Checkout URL redirect (new subscription)
      if (data?.url) {
        const { openExternalUrl } = await import("@/lib/openExternalUrl");
        await openExternalUrl(data.url);
        return;
      }

      if (data?.alreadyOnPlan) {
        toast.info("You are already on this plan");
        onOpenChange(false);
        return;
      }

      throw new Error("No checkout URL received");
    } catch (error: any) {
      console.error("Plan change error:", error);
      toast.error("Failed to change plan", {
        description: error.message || "Please try again later",
      });
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  };

  const renewalLabel = renewalDateFormatted || "your next renewal date";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDowngrade ? "Confirm downgrade" : "Confirm plan change"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2">
              {isDowngrade ? (
                <>
                  <p>Your plan will change on <strong>{renewalLabel}</strong>.</p>
                  <p>You'll keep your current features until then.</p>
                  <p>You <strong>won't be charged today</strong>.</p>
                  <p>From {renewalLabel} you'll pay <strong>{formatCurrency(tier.price)}</strong> every 30 days unless you cancel.</p>
                  <p className="text-xs text-muted-foreground pt-1">
                    No charge today · Change takes effect at renewal · Cancel anytime
                  </p>
                </>
              ) : isTrialing ? (
                <>
                  <p>Switching to <strong>{tier.name}</strong> ({formatCurrency(tier.price)}/month) takes effect immediately.</p>
                  <p>You won't be charged today because you're still on trial.</p>
                  <p>You'll be charged {formatCurrency(tier.price)} when your trial ends, then renew every 30 days unless you cancel.</p>
                  <p className="text-xs text-muted-foreground pt-1">
                    No charge today · Renews every 30 days after trial · Cancel anytime
                  </p>
                </>
              ) : (
                <>
                  <p>Switching to <strong>{tier.name}</strong> ({formatCurrency(tier.price)}/month) takes effect immediately.</p>
                  <p>You will be charged <strong>{formatCurrency(tier.price)} today</strong> and your billing date will reset to today.</p>
                  <p>Your next renewal will be 30 days from today.</p>
                  <p>Then it renews every 30 days at {formatCurrency(tier.price)} unless you cancel.</p>
                  <p className="text-xs text-muted-foreground pt-1">
                    Charges today · Renews every 30 days · Cancel anytime
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={loading} className="gap-2">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {statusText}
              </>
            ) : isDowngrade ? (
              <>
                <TrendingDown className="h-4 w-4" />
                Confirm downgrade
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4" />
                {isTrialing
                  ? "Confirm upgrade"
                  : `Confirm & pay ${formatCurrency(tier.price)} today`}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** @deprecated Use PlanChangeConfirmDialog instead */
export const UpgradeConfirmDialog = PlanChangeConfirmDialog;
